import type { SupportCategory } from '@cottonray/support-contract'

/**
 * 문의 화면 문구 — **9개 언어의 단일 출처**.
 *
 * 문의 화면이 앱 안에서 웹으로 옮겨 오면서(2026-08-26 사용자 지시: "문의하기를 웹사이트로
 * 배포하고. cottonray.com 에 하나로") 문구도 여기로 왔다. 앱마다 `t()` 로 만든 값을 공용
 * 화면에 넘기던 구조는 **문구 한 줄을 고치려면 앱을 다시 심사에 올려야 했다** — 그 구조를
 * 없애는 것이 이 이전의 목적이다.
 *
 * 번역은 지어낸 것이 아니라 이지에어스크린이 쓰던 `src/i18n/locales/*.ts` 의 `support` 블록을
 * 그대로 옮긴 것이다. 그 앱만 9개 언어를 갖고 있었고, 이제 전 서비스가 같은 문장을 쓴다.
 *
 * **`{...}` 자리는 `fill()` 이 채운다** — 언어마다 어순이 달라 문자열을 이어 붙일 수 없다.
 */
export interface SupportLabels {
  title: string
  intro: string
  categoryLabel: string
  categoryName: Record<SupportCategory, string>
  messageLabel: string
  messagePlaceholder: string
  /** `{min}` — 최소 글자 수 */
  messageTooShort: string
  /** `{count}` `{max}` */
  messageCounter: string
  emailLabel: string
  emailPlaceholder: string
  emailHelper: string
  emailInvalid: string
  photoLabel: string
  photoAdd: string
  /** `{count}` `{max}` */
  photoCounter: string
  photoRemove: string
  photoPrivacyNote: string
  photoRejectedType: string
  /** `{max}` — 한 장 상한(MB) */
  photoRejectedSize: string
  /**
   * 앱에서 열렸을 때. **무엇이 함께 나가는지 밝히는 줄이라 방침의 수집 항목과 같아야 한다.**
   */
  privacyNote: string
  /**
   * 브라우저에서 주소를 직접 열었을 때 — 앱이 넘긴 환경 정보가 없으므로 "앱 버전·기기
   * 모델이 함께 전달된다"고 적으면 **거짓말이 된다.** 그래서 그 문장만 뺀 판을 따로 둔다.
   */
  privacyNoteWeb: string
  privacyLink: string
  submit: string
  sending: string
  retry: string
  sentTitle: string
  sentBody: string
  /** `{id}` — 접수번호 앞자리 */
  receipt: string
  /**
   * 보내고 난 뒤. **`window.close()` 를 쓰지 않는다** — 스크립트가 연 창이 아니면 브라우저가
   * 무시하고, 앱이 띄운 커스텀 탭·사파리 뷰에서는 아무 일도 일어나지 않는다. 사람에게
   * 닫으라고 말하는 편이 정직하다.
   */
  closeHint: string
  errorRateLimited: string
  errorInvalid: string
  errorNetwork: string
  errorServer: string
  errorTooLarge: string
}

export const SUPPORT_LANGUAGES = ['ko', 'en', 'ja', 'de', 'es', 'fr', 'pt', 'ru', 'zh'] as const

export type SupportLanguage = (typeof SUPPORT_LANGUAGES)[number]

const ko: SupportLabels = {
  title: '지원 문의',
  intro:
    '불편한 점이나 바라는 기능을 알려주세요. 어떤 상황에서 그랬는지 함께 적어주시면 훨씬 빨리 찾을 수 있어요. 메일 주소를 남겨주시면 답장도 보내드려요.',
  categoryLabel: '문의 종류',
  categoryName: {
    BUG: '오류·버그',
    IDEA: '기능 제안',
    ACCOUNT: '계정·로그인',
    ADS: '광고',
    ETC: '기타',
  },
  messageLabel: '문의 내용',
  messagePlaceholder: '예: 알림 시간을 3일 전으로 맞췄는데 당일 아침에 왔어요.',
  messageTooShort: '{min}자 이상 적어주세요.',
  messageCounter: '{count}/{max}자',
  emailLabel: '답장받을 메일 주소 (선택)',
  emailPlaceholder: 'name@example.com',
  emailHelper: '적어주시면 확인한 뒤 답장을 보내드려요. 안 적으셔도 내용은 빠짐없이 읽어요.',
  emailInvalid: '메일 주소를 다시 확인해주세요.',
  photoLabel: '사진 첨부 (선택)',
  photoAdd: '사진 고르기',
  photoCounter: '{count}/{max}장',
  photoRemove: '사진 빼기',
  photoPrivacyNote:
    '화면이 어떻게 보이는지 사진으로 보여주시면 훨씬 빨리 찾을 수 있어요. 개인정보가 담긴 화면은 빼고 골라주세요. 사진은 문의와 함께 1년 뒤에 지워요.',
  photoRejectedType: 'jpg·png·webp 사진만 붙일 수 있어요.',
  photoRejectedSize: '사진 한 장은 {max}MB까지 붙일 수 있어요.',
  privacyNote:
    '보내면 앱 버전·기기 모델·OS 버전·언어 설정이 함께 전달돼요. 적어주신 메일 주소는 답장에만 쓰고, 접수한 의견은 1년 뒤에 지워요.',
  privacyNoteWeb: '적어주신 메일 주소는 답장에만 쓰고, 접수한 의견은 1년 뒤에 지워요.',
  privacyLink: '개인정보처리방침',
  submit: '보내기',
  sending: '보내는 중…',
  retry: '다시 시도',
  sentTitle: '의견을 잘 받았어요',
  sentBody:
    '보내주신 내용은 그대로 확인하고 다음 업데이트에 반영할게요. 메일 주소를 적어주셨으면 답장도 보내드릴게요.',
  receipt: '접수번호 {id}',
  closeHint: '이 창은 닫으셔도 돼요.',
  errorRateLimited: '문의를 너무 자주 보냈어요. 잠시 뒤 다시 시도해주세요.',
  errorInvalid: '보낸 내용을 확인해주세요. 너무 짧은지도 함께 봐주세요.',
  errorNetwork: '네트워크에 연결할 수 없어요. 연결을 확인하고 다시 시도해주세요.',
  errorServer: '지금은 접수할 수 없어요. 잠시 뒤 다시 시도해주세요.',
  errorTooLarge: '사진 용량이 너무 커요. 사진을 빼고 다시 시도해주세요.',
}

const en: SupportLabels = {
  title: 'Support',
  intro:
    'Tell us what went wrong or what you would like to see. Describing when it happened helps us find it much faster. If you leave an email address, we will reply too.',
  categoryLabel: 'Topic',
  categoryName: {
    BUG: 'Bug',
    IDEA: 'Feature idea',
    ACCOUNT: 'Account & sign-in',
    ADS: 'Ads',
    ETC: 'Other',
  },
  messageLabel: 'Your message',
  messagePlaceholder:
    'e.g. I set the reminder to three days before, but it arrived on the morning of the day.',
  messageTooShort: 'Please write at least {min} characters.',
  messageCounter: '{count}/{max}',
  emailLabel: 'Email for replies (optional)',
  emailPlaceholder: 'name@example.com',
  emailHelper:
    'Enter one to receive a reply. We still read every message if you leave it blank.',
  emailInvalid: 'Enter a valid email address.',
  photoLabel: 'Attach photos (optional)',
  photoAdd: 'Choose photos',
  photoCounter: '{count}/{max}',
  photoRemove: 'Remove photo',
  photoPrivacyNote:
    'Screenshots help us find the cause faster. Please leave out screens with personal information. Photos are deleted with your message after one year.',
  photoRejectedType: 'Only jpg, png or webp photos can be attached.',
  photoRejectedSize: 'Each photo can be up to {max}MB.',
  privacyNote:
    'Your app version, device model, OS version and language are sent with your message. We use the email address you provide only to reply, and delete your message after one year.',
  privacyNoteWeb:
    'We use the email address you provide only to reply, and delete your message after one year.',
  privacyLink: 'Privacy policy',
  submit: 'Send',
  sending: 'Sending…',
  retry: 'Try again',
  sentTitle: 'Thanks — we got it',
  sentBody:
    'We will read every word and use it in the next update. If you added an email address, we will reply too.',
  receipt: 'Reference {id}',
  closeHint: 'You can close this window now.',
  errorRateLimited: 'You have sent too many messages. Please try again later.',
  errorInvalid: 'Please check your message — it may be too short.',
  errorNetwork: 'Cannot reach the network. Check your connection and try again.',
  errorServer: 'We cannot receive messages right now. Please try again later.',
  errorTooLarge: 'A photo is too large to send. Remove it and try again.',
}

const ja: SupportLabels = {
  title: 'お問い合わせ',
  intro:
    'うまく動かないことや、こうしてほしいことをお知らせください。どんなときに起きたかも書いていただけると、早く見つけられます。メールアドレスをご入力いただければ、ご返信もお送りします。',
  categoryLabel: '種類',
  categoryName: {
    BUG: '不具合',
    IDEA: '機能のご要望',
    ACCOUNT: 'アカウント・ログイン',
    ADS: '広告',
    ETC: 'その他',
  },
  messageLabel: 'お問い合わせ内容',
  messagePlaceholder: '例: 通知を3日前に設定したのに、当日の朝に届きました。',
  messageTooShort: '{min}文字以上で入力してください。',
  messageCounter: '{count}/{max}文字',
  emailLabel: '返信先メールアドレス（任意）',
  emailPlaceholder: 'name@example.com',
  emailHelper: 'ご入力いただくと、確認後にご返信します。未入力でも内容はすべて拝見します。',
  emailInvalid: 'メールアドレスの形式をご確認ください。',
  photoLabel: '写真添付（任意）',
  photoAdd: '写真を選択',
  photoCounter: '{count}/{max}枚',
  photoRemove: '添付写真を外す',
  photoPrivacyNote:
    '画面の写真があると、原因を早く見つけられます。個人情報が写った画面は選ばないでください。写真もお問い合わせとともに1年後に削除します。',
  photoRejectedType: '添付できる写真はjpg・png・webpのみです。',
  photoRejectedSize: '写真1枚につき{max}MBまで添付できます。',
  privacyNote:
    '送信するとアプリのバージョン・端末の機種・OSバージョン・言語設定も一緒に送られます。ご入力いただいたメールアドレスは返信にのみ使用し、お問い合わせは1年後に削除します。',
  privacyNoteWeb:
    'ご入力いただいたメールアドレスは返信にのみ使用し、お問い合わせは1年後に削除します。',
  privacyLink: 'プライバシーポリシー',
  submit: '送信',
  sending: '送信中…',
  retry: '再試行',
  sentTitle: 'ご意見を受け取りました',
  sentBody:
    'いただいた内容を確認し、今後の更新に活かします。メールアドレスをご入力いただいた場合は、ご返信もお送りします。',
  receipt: '受付番号 {id}',
  closeHint: 'この画面は閉じていただいて大丈夫です。',
  errorRateLimited: '短時間に送信しすぎました。しばらくしてからお試しください。',
  errorInvalid: '内容をご確認ください。短すぎないかもあわせてご確認ください。',
  errorNetwork: 'ネットワークに接続できません。接続を確認して再度お試しください。',
  errorServer: '現在受け付けできません。しばらくしてからお試しください。',
  errorTooLarge: '写真の容量が大きすぎて送信できません。写真を外して再度お試しください。',
}

const de: SupportLabels = {
  title: 'Support',
  intro:
    'Schreib uns, was nicht funktioniert oder was du dir wünschst. Beschreibe auch, wann es passiert — so finden wir die Ursache schneller. Wenn du deine E-Mail-Adresse angibst, antworten wir dir auch.',
  categoryLabel: 'Thema',
  categoryName: {
    BUG: 'Fehler',
    IDEA: 'Funktionswunsch',
    ACCOUNT: 'Konto & Anmeldung',
    ADS: 'Werbung',
    ETC: 'Sonstiges',
  },
  messageLabel: 'Deine Nachricht',
  messagePlaceholder:
    'z. B.: Ich habe die Erinnerung auf drei Tage vorher gestellt, sie kam aber erst am Morgen des Tages.',
  messageTooShort: 'Bitte schreibe mindestens {min} Zeichen.',
  messageCounter: '{count}/{max}',
  emailLabel: 'E-Mail-Adresse für Antworten (optional)',
  emailPlaceholder: 'name@example.com',
  emailHelper: 'Gib sie an, wenn du eine Antwort möchtest. Auch ohne Adresse lesen wir alles.',
  emailInvalid: 'Bitte gib eine gültige E-Mail-Adresse ein.',
  photoLabel: 'Fotos anhängen (optional)',
  photoAdd: 'Fotos auswählen',
  photoCounter: '{count}/{max}',
  photoRemove: 'Foto entfernen',
  photoPrivacyNote:
    'Fotos vom Bildschirm helfen uns, die Ursache schneller zu finden. Wähle keine Bilder mit persönlichen Daten aus. Auch die Fotos werden mit deiner Nachricht nach einem Jahr gelöscht.',
  photoRejectedType: 'Nur Fotos im Format jpg, png oder webp können angehängt werden.',
  photoRejectedSize: 'Ein Foto darf höchstens {max}MB groß sein.',
  privacyNote:
    'Mit deiner Nachricht werden App-Version, Gerätemodell, Systemversion und Sprache gesendet. Deine E-Mail-Adresse verwenden wir nur für die Antwort; deine Nachricht wird nach einem Jahr gelöscht.',
  privacyNoteWeb:
    'Deine E-Mail-Adresse verwenden wir nur für die Antwort; deine Nachricht wird nach einem Jahr gelöscht.',
  privacyLink: 'Datenschutzerklärung',
  submit: 'Senden',
  sending: 'Wird gesendet …',
  retry: 'Erneut versuchen',
  sentTitle: 'Angekommen — danke',
  sentBody:
    'Wir lesen deine Nachricht und berücksichtigen sie im nächsten Update. Wenn du deine E-Mail-Adresse angegeben hast, antworten wir dir auch.',
  receipt: 'Vorgang {id}',
  closeHint: 'Du kannst dieses Fenster jetzt schließen.',
  errorRateLimited: 'Zu viele Nachrichten gesendet. Bitte später erneut versuchen.',
  errorInvalid: 'Bitte prüfe deine Nachricht — vielleicht ist sie zu kurz.',
  errorNetwork: 'Kein Netzwerk erreichbar. Verbindung prüfen und erneut versuchen.',
  errorServer: 'Wir können gerade nichts entgegennehmen. Bitte später erneut versuchen.',
  errorTooLarge: 'Ein Foto ist zu groß zum Senden. Entferne es und versuche es erneut.',
}

const es: SupportLabels = {
  title: 'Soporte',
  intro:
    'Cuéntanos qué no funciona o qué te gustaría tener. Si explicas cuándo ocurre, lo encontramos mucho antes. Si dejas tu correo, también te responderemos.',
  categoryLabel: 'Tipo',
  categoryName: {
    BUG: 'Error',
    IDEA: 'Idea de función',
    ACCOUNT: 'Cuenta e inicio de sesión',
    ADS: 'Anuncios',
    ETC: 'Otro',
  },
  messageLabel: 'Tu mensaje',
  messagePlaceholder:
    'Ej.: puse el aviso para tres días antes, pero llegó la mañana del mismo día.',
  messageTooShort: 'Escribe al menos {min} caracteres.',
  messageCounter: '{count}/{max}',
  emailLabel: 'Correo para recibir respuesta (opcional)',
  emailPlaceholder: 'name@example.com',
  emailHelper:
    'Escríbelo si quieres recibir una respuesta. Aunque lo dejes en blanco, leeremos tu mensaje.',
  emailInvalid: 'Introduce un correo válido.',
  photoLabel: 'Adjuntar fotos (opcional)',
  photoAdd: 'Elegir fotos',
  photoCounter: '{count}/{max}',
  photoRemove: 'Quitar foto',
  photoPrivacyNote:
    'Las fotos de la pantalla nos ayudan a encontrar antes la causa. No elijas imágenes con datos personales. También se borran con el mensaje al cabo de un año.',
  photoRejectedType: 'Solo se pueden adjuntar fotos jpg, png o webp.',
  photoRejectedSize: 'Cada foto puede ocupar hasta {max}MB.',
  privacyNote:
    'Con el mensaje se envían la versión de la app, el modelo del dispositivo, la versión del sistema y el idioma. El correo que indiques solo se usará para responder y la consulta se borrará al cabo de un año.',
  privacyNoteWeb:
    'El correo que indiques solo se usará para responder y la consulta se borrará al cabo de un año.',
  privacyLink: 'Política de privacidad',
  submit: 'Enviar',
  sending: 'Enviando…',
  retry: 'Reintentar',
  sentTitle: 'Recibido, gracias',
  sentBody:
    'Leeremos tu mensaje y lo tendremos en cuenta en la próxima actualización. Si indicaste tu correo, también te responderemos.',
  receipt: 'Referencia {id}',
  closeHint: 'Ya puedes cerrar esta ventana.',
  errorRateLimited: 'Has enviado demasiados mensajes. Inténtalo más tarde.',
  errorInvalid: 'Revisa tu mensaje — puede que sea demasiado corto.',
  errorNetwork: 'No se puede conectar a la red. Comprueba la conexión e inténtalo de nuevo.',
  errorServer: 'Ahora mismo no podemos recibir mensajes. Inténtalo más tarde.',
  errorTooLarge: 'Una foto es demasiado grande para enviarla. Quítala e inténtalo de nuevo.',
}

const fr: SupportLabels = {
  title: 'Assistance',
  intro:
    'Dites-nous ce qui ne fonctionne pas ou ce que vous aimeriez. Préciser le moment où cela arrive nous aide à trouver bien plus vite. Laissez votre adresse e-mail et nous vous répondrons aussi.',
  categoryLabel: 'Sujet',
  categoryName: {
    BUG: 'Bogue',
    IDEA: 'Idée de fonctionnalité',
    ACCOUNT: 'Compte et connexion',
    ADS: 'Publicités',
    ETC: 'Autre',
  },
  messageLabel: 'Votre message',
  messagePlaceholder:
    'Ex. : j’ai réglé le rappel trois jours avant, mais il est arrivé le matin même.',
  messageTooShort: 'Écrivez au moins {min} caractères.',
  messageCounter: '{count}/{max}',
  emailLabel: 'E-mail pour la réponse (facultatif)',
  emailPlaceholder: 'name@example.com',
  emailHelper: 'Indiquez-la pour recevoir une réponse. Même sans adresse, nous lirons tout.',
  emailInvalid: 'Saisissez une adresse e-mail valide.',
  photoLabel: 'Joindre des photos (facultatif)',
  photoAdd: 'Choisir des photos',
  photoCounter: '{count}/{max}',
  photoRemove: 'Retirer la photo',
  photoPrivacyNote:
    'Les photos de l’écran nous aident à trouver la cause plus vite. Ne choisissez pas de photo avec des données personnelles. Elles seront supprimées avec votre message au bout d’un an.',
  photoRejectedType: 'Seules les photos jpg, png et webp peuvent être jointes.',
  photoRejectedSize: 'Chaque photo est limitée à {max}MB.',
  privacyNote:
    'La version de l’app, le modèle de l’appareil, la version du système et la langue sont envoyés avec votre message. Votre adresse e-mail sert uniquement à vous répondre, et votre demande est supprimée au bout d’un an.',
  privacyNoteWeb:
    'Votre adresse e-mail sert uniquement à vous répondre, et votre demande est supprimée au bout d’un an.',
  privacyLink: 'Politique de confidentialité',
  submit: 'Envoyer',
  sending: 'Envoi…',
  retry: 'Réessayer',
  sentTitle: 'Bien reçu, merci',
  sentBody:
    'Nous lirons votre message et en tiendrons compte dans la prochaine mise à jour. Si vous avez indiqué votre e-mail, nous vous répondrons aussi.',
  receipt: 'Référence {id}',
  closeHint: 'Vous pouvez fermer cette fenêtre.',
  errorRateLimited: 'Trop de messages envoyés. Réessayez plus tard.',
  errorInvalid: 'Vérifiez votre message — il est peut-être trop court.',
  errorNetwork: 'Réseau injoignable. Vérifiez votre connexion et réessayez.',
  errorServer: 'Impossible de recevoir votre message pour le moment. Réessayez plus tard.',
  errorTooLarge: 'Une photo est trop volumineuse pour être envoyée. Retirez-la et réessayez.',
}

const pt: SupportLabels = {
  title: 'Suporte',
  intro:
    'Conte o que não está funcionando ou o que você gostaria de ter. Dizer quando acontece ajuda muito a encontrar o problema. Se deixar seu e-mail, você também receberá uma resposta.',
  categoryLabel: 'Assunto',
  categoryName: {
    BUG: 'Erro',
    IDEA: 'Sugestão de função',
    ACCOUNT: 'Conta e login',
    ADS: 'Anúncios',
    ETC: 'Outro',
  },
  messageLabel: 'Sua mensagem',
  messagePlaceholder:
    'Ex.: configurei o aviso para três dias antes, mas ele chegou na manhã do dia.',
  messageTooShort: 'Escreva pelo menos {min} caracteres.',
  messageCounter: '{count}/{max}',
  emailLabel: 'E-mail para resposta (opcional)',
  emailPlaceholder: 'name@example.com',
  emailHelper:
    'Informe para receber uma resposta. Mesmo sem e-mail, vamos ler toda a mensagem.',
  emailInvalid: 'Informe um e-mail válido.',
  photoLabel: 'Anexar fotos (opcional)',
  photoAdd: 'Escolher fotos',
  photoCounter: '{count}/{max}',
  photoRemove: 'Remover foto',
  photoPrivacyNote:
    'Fotos da tela ajudam a encontrar a causa mais rápido. Escolha apenas imagens sem dados pessoais. As fotos também são apagadas com a mensagem após um ano.',
  photoRejectedType: 'Só é possível anexar fotos jpg, png ou webp.',
  photoRejectedSize: 'Cada foto pode ter até {max}MB.',
  privacyNote:
    'São enviados com a mensagem a versão do app, o modelo do aparelho, a versão do sistema e o idioma. O e-mail informado é usado apenas para responder, e a mensagem é apagada após um ano.',
  privacyNoteWeb:
    'O e-mail informado é usado apenas para responder, e a mensagem é apagada após um ano.',
  privacyLink: 'Política de privacidade',
  submit: 'Enviar',
  sending: 'Enviando…',
  retry: 'Tentar de novo',
  sentTitle: 'Recebido, obrigado',
  sentBody:
    'Vamos ler sua mensagem e considerá-la na próxima atualização. Se você informou seu e-mail, também enviaremos uma resposta.',
  receipt: 'Protocolo {id}',
  closeHint: 'Você já pode fechar esta janela.',
  errorRateLimited: 'Você enviou mensagens demais. Tente novamente mais tarde.',
  errorInvalid: 'Confira sua mensagem — pode estar curta demais.',
  errorNetwork: 'Sem conexão de rede. Verifique e tente novamente.',
  errorServer: 'Não é possível receber mensagens agora. Tente mais tarde.',
  errorTooLarge: 'Uma foto é grande demais para enviar. Remova-a e tente de novo.',
}

const ru: SupportLabels = {
  title: 'Поддержка',
  intro:
    'Расскажите, что не работает или чего не хватает. Если укажете, когда это происходит, мы быстрее найдём причину. Оставьте адрес почты, и мы также ответим.',
  categoryLabel: 'Тема',
  categoryName: {
    BUG: 'Ошибка',
    IDEA: 'Идея функции',
    ACCOUNT: 'Аккаунт и вход',
    ADS: 'Реклама',
    ETC: 'Другое',
  },
  messageLabel: 'Ваше сообщение',
  messagePlaceholder:
    'Например: я поставил напоминание за три дня, а оно пришло утром в тот же день.',
  messageTooShort: 'Напишите не менее {min} символов.',
  messageCounter: '{count}/{max}',
  emailLabel: 'Адрес почты для ответа (необязательно)',
  emailPlaceholder: 'name@example.com',
  emailHelper: 'Укажите его, чтобы получить ответ. Даже без адреса мы прочитаем всё сообщение.',
  emailInvalid: 'Введите корректный адрес электронной почты.',
  photoLabel: 'Прикрепить фото (необязательно)',
  photoAdd: 'Выбрать фото',
  photoCounter: '{count}/{max}',
  photoRemove: 'Убрать фото',
  photoPrivacyNote:
    'Фото экрана помогут быстрее найти причину. Не выбирайте снимки с личными данными. Фото тоже будут удалены вместе с обращением через год.',
  photoRejectedType: 'Можно прикрепить только фото в форматах jpg, png и webp.',
  photoRejectedSize: 'Размер одного фото — не более {max}MB.',
  privacyNote:
    'Вместе с сообщением отправляются версия приложения, модель устройства, версия системы и язык. Указанный адрес почты используется только для ответа, а обращение удаляется через год.',
  privacyNoteWeb:
    'Указанный адрес почты используется только для ответа, а обращение удаляется через год.',
  privacyLink: 'Политика конфиденциальности',
  submit: 'Отправить',
  sending: 'Отправка…',
  retry: 'Повторить',
  sentTitle: 'Получено, спасибо',
  sentBody:
    'Мы прочитаем сообщение и учтём его в следующем обновлении. Если вы указали адрес почты, мы также ответим.',
  receipt: 'Номер обращения {id}',
  closeHint: 'Это окно можно закрыть.',
  errorRateLimited: 'Слишком много сообщений. Попробуйте позже.',
  errorInvalid: 'Проверьте сообщение — возможно, оно слишком короткое.',
  errorNetwork: 'Нет соединения с сетью. Проверьте подключение и повторите.',
  errorServer: 'Сейчас мы не можем принять сообщение. Попробуйте позже.',
  errorTooLarge: 'Фото слишком большое для отправки. Уберите его и попробуйте снова.',
}

const zh: SupportLabels = {
  title: '联系支持',
  intro:
    '请告诉我们哪里不好用，或者你希望增加什么。写清楚出现问题的场景，我们能更快定位。留下邮箱地址，我们也会回复你。',
  categoryLabel: '问题类型',
  categoryName: {
    BUG: '故障',
    IDEA: '功能建议',
    ACCOUNT: '账号与登录',
    ADS: '广告',
    ETC: '其他',
  },
  messageLabel: '问题描述',
  messagePlaceholder: '例如：我把提醒设成提前三天，结果当天早上才收到。',
  messageTooShort: '请至少输入 {min} 个字。',
  messageCounter: '{count}/{max}',
  emailLabel: '回复邮箱（选填）',
  emailPlaceholder: 'name@example.com',
  emailHelper: '填写后即可收到回复；不填也没关系，每条反馈我们都会看。',
  emailInvalid: '邮箱地址格式不正确。',
  photoLabel: '添加照片（选填）',
  photoAdd: '选择照片',
  photoCounter: '{count}/{max}张',
  photoRemove: '移除照片',
  photoPrivacyNote:
    '屏幕照片能帮助我们更快找到原因。请选择不含个人信息的画面。照片也会随反馈在一年后删除。',
  photoRejectedType: '仅支持jpg、png和webp格式的照片。',
  photoRejectedSize: '每张照片最大为{max}MB。',
  privacyNote:
    '提交时会一并发送应用版本、设备型号、系统版本和语言设置。你填写的邮箱地址只用于回复，反馈会在一年后删除。',
  privacyNoteWeb: '你填写的邮箱地址只用于回复，反馈会在一年后删除。',
  privacyLink: '隐私政策',
  submit: '发送',
  sending: '正在发送…',
  retry: '重试',
  sentTitle: '已收到你的意见',
  sentBody: '我们会认真查看并用于后续更新。填写了邮箱地址的话，我们也会回复你。',
  receipt: '受理编号 {id}',
  closeHint: '现在可以关闭此页面了。',
  errorRateLimited: '提交太频繁了，请稍后再试。',
  errorInvalid: '请检查反馈内容，也看看是不是太短了。',
  errorNetwork: '无法连接网络，请检查后重试。',
  errorServer: '目前无法受理，请稍后再试。',
  errorTooLarge: '照片太大，无法发送。请移除照片后重试。',
}

const LABELS: Record<SupportLanguage, SupportLabels> = { ko, en, ja, de, es, fr, pt, ru, zh }

/**
 * 문구 한 벌을 고른다. **기본은 영어다** — 게임·앱의 기본 언어가 영어이고
 * (docs/rules.md), 모르는 언어로 온 사람에게 한국어를 보여 주는 것보다 낫다.
 *
 * `ko-KR`·`zh-Hans-CN` 처럼 지역이 붙은 태그도 앞자리로 받는다 — 앱이 넘기는 값이
 * `expo-localization` 의 `languageTag` 라 대부분 지역이 붙어 있다.
 */
export function resolveLabels(tag: string | undefined): SupportLabels {
  return LABELS[languageOf(tag)]
}

export function languageOf(tag: string | undefined): SupportLanguage {
  const primary = tag?.trim().toLowerCase().split(/[-_]/)[0]
  return SUPPORT_LANGUAGES.find((language) => language === primary) ?? 'en'
}

/** `{name}` 자리를 채운다. 없는 이름은 그대로 둔다 — 문구가 깨진 것이 눈에 보여야 고친다 */
export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in values ? String(values[name]) : whole,
  )
}
